/**
 * Curated icon surface. Kept OUT of the main `@ui` barrel so importing one icon never
 * pulls the whole set (ARCHITECTURE §2). Import from '@ui/icons'.
 *
 * The Figma file mixes several icon sets (hugeicons, material-symbols, iconoir, eva,
 * mdi, ic). We standardize on a single set — lucide — and re-export only the glyphs the
 * design uses. Add new icons here as components need them; never import lucide directly.
 */
export {
  // actions / controls
  Plus,
  Minus,
  X,
  Check,
  CheckCheck,
  Search,
  Locate,
  MapPin,
  Filter,
  Download,
  Upload,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  FileText,
  MoreVertical,
  Menu,
  Paperclip,
  // navigation / chevrons
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  ArrowLeft,
  ArrowRight,
  ArrowLeftRight,
  ArrowUpRight,
  // status / feedback
  Info,
  Lightbulb,
  CircleAlert,
  CircleCheck,
  // domain
  Archive,
  Users,
  User,
  Receipt,
  Scale,
  Wallet,
  Tag,
  Crown,
  Calendar,
  Mail,
  Settings,
  // theme / loading
  Sun,
  Moon,
  LoaderCircle,
  CloudUpload,
  // stats and dashboard
  TrendingUp,
} from 'lucide-react';

export type { LucideIcon, LucideProps } from 'lucide-react';

import createIcon from './icons/createIcon';

import DoubleArrowPrevSVG from '@shared/assets/icons/DoubleArrowPrev.svg?react';
import DoubleCheckIconSVG from '@shared/assets/icons/DoubleCheckIcon.svg?react';
import LogoutIconSVG from '@shared/assets/icons/LogoutIcon.svg?react';
import NavArrowIconSVG from '@shared/assets/icons/NavArrowIcon.svg?react';
import SettlupIconSVG from '@shared/assets/icons/SettlupIcon.svg?react';
import SplitAmountIconSVG from '@shared/assets/icons/SplitAmountIcon.svg?react';
import SplitEvenlyIconSVG from '@shared/assets/icons/SplitEvenlyIcon.svg?react';
import SplitPercentageIconSVG from '@shared/assets/icons/SplitPercentageIcon.svg?react';
import SplitSharesIconSVG from '@shared/assets/icons/SplitSharesIcon.svg?react';
import UploadCustomSVG from '@shared/assets/icons/UploadCustom.svg?react';
import AllListSVG from '@shared/assets/icons/AllList.svg?react';
import BreakdownSVG from '@shared/assets/icons/Breakdown.svg?react';
import BreakupSVG from '@shared/assets/icons/Breakup.svg?react';
import LocationSVG from '@shared/assets/icons/Location.svg?react';
import ReceiptSVG from '@shared/assets/icons/Receipt.svg?react';
import UploadSVG from '@shared/assets/icons/Upload.svg?react';
import HourglassTiltedSVG from '@shared/assets/icons/HourglassTilted.svg?react';
import UnassignedSVG from '@shared/assets/icons/unassigned.svg?react';
import ListIconSVG from '@shared/assets/icons/ListIcon.svg?react';
import GoogleIconSVG from '@shared/assets/icons/GooglIcon.svg?react';
import AppleIconSVG from '@shared/assets/icons/AppleIcon.svg?react';
import AddPlusSVG from '@shared/assets/icons/AddPlus.svg?react';
import RoundArrowBackSVG from '@shared/assets/icons/round-arrow-back.svg?react';
import checkboxTickSVG from '@shared/assets/icons/checkboxcheckicon.svg?react';
import GroupTypeFriendsSVG from '@shared/assets/icons/group-type-friends.svg?react';
import GroupTypeHomeSVG from '@shared/assets/icons/group-type-home.svg?react';
import GroupTypeOfficeSVG from '@shared/assets/icons/group-type-office.svg?react';
import GroupTypeOthersSVG from '@shared/assets/icons/group-type-others.svg?react';
import GroupTypeRoommatesSVG from '@shared/assets/icons/group-type-roommates.svg?react';
import GroupTypeTripSVG from '@shared/assets/icons/group-type-trip.svg?react';
import UploadFolderSVG from '@shared/assets/icons/FolderUploadIcon.svg?react';
import DownArrowSVG from '@shared/assets/icons/DownArrow.svg?react';
import UpArrowSVG from '@shared/assets/icons/UpArrow.svg?react';
import RightArrowSVG from '@shared/assets/icons/RightArrow.svg?react';
import recycledollarSVG from '@shared/assets/icons/recycledollar.svg?react';
import reportWarningSVG from '@shared/assets/icons/ReportWarning.svg?react';
import MailEnvelopeSVG from '@shared/assets/icons/MailEnvelope.svg?react';
import HistorySVG from '@shared/assets/icons/History.svg?react';
import HistoryClockwiseSVG from '@shared/assets/icons/HistoryClockwise.svg?react';
import RepeatIconSVG from '@shared/assets/icons/RepeatIcon.svg?react';
import FoodIconSVG from '@shared/assets/icons/FoodIcon.svg?react';
import NavBarchartIconSVG from '@shared/assets/icons/NavBarchartIcon.svg?react';
import NavClockIconSVG from '@shared/assets/icons/NavClockIcon.svg?react';
import NavExclamationIconSVG from '@shared/assets/icons/NavExclamationIcon.svg?react';
import NavHourglassIconSVG from '@shared/assets/icons/NavHourglassIcon.svg?react';
import NavListpadIconSVG from '@shared/assets/icons/NavListpadIcon.svg?react';
import NavQuestionIconSVG from '@shared/assets/icons/NavQuestionIcon.svg?react';
import WalletIconSVG from '@shared/assets/icons/WalletIcon.svg?react';
import RepeatCalanderIconSVG from '@shared/assets/icons/RepeatCalanderIcon.svg?react';
import SnacksIconSVG from '@shared/assets/icons/SnacksIcon.svg?react';
import GroceriesIconSVG from '@shared/assets/icons/GroceriesIcon.svg?react';
import LiquorIconSVG from '@shared/assets/icons/LiquorIcon.svg?react';
import HouseIconSVG from '@shared/assets/icons/HouseIcon.svg?react';
import PowerIconSVG from '@shared/assets/icons/PowerIcon.svg?react';
import DiningIconSVG from '@shared/assets/icons/DiningIcon.svg?react';
import ExcelIconSVG from '@shared/assets/icons/ExcelIcon.svg?react';
import LightBulbOnSVG from '@shared/assets/icons/LightBulbOn.svg?react';
import InfoInCircleSVG from '@shared/assets/icons/InfoInCircle.svg?react';
import ThreeArrowForwordSVG from '@shared/assets/icons/TripalArrowForword.svg?react';
import SingleStarSVG from '@shared/assets/icons/SingleStar.svg?react';
import HeartSVG from '@shared/assets/icons/Heart.svg?react';
import ActiveGroupStateSVG from '@shared/assets/icons/active_group_state.svg?react';
import MonthlySettleStateSVG from '@shared/assets/icons/monthly_settel_state.svg?react';
import MonthlySpendingStateSVG from '@shared/assets/icons/monthly_spending_state.svg?react';
import PendingStatementStateSVG from '@shared/assets/icons/pending_statement_state.svg?react';
import SettlementReceivedSVG from '@shared/assets/icons/settlement-received-icon.svg?react';
import SettlementPaidSVG from '@shared/assets/icons/settlement-paid-icon.svg?react';
import OtherExpenseSVG from '@shared/assets/icons/other-expance-icon.svg?react';
import FundsAllocationIconSVG from '@shared/assets/icons/funds-allocation-icon.svg?react';
import FundsExchangeIconSVG from '@shared/assets/icons/funds-exchange-icon.svg?react';
import WalletDepositIconSVG from '@shared/assets/icons/wallet-deposit-icon.svg?react';
import RepeatIconSVG2 from '@shared/assets/icons/repeat_icon.svg?react';
import tickCheckIconSVG from '@shared/assets/icons/tick-check-icon.svg?react';

import ActivityAddedSVG from '@shared/assets/icons/activity_added.svg?react';
import ActivityAddMemberSVG from '@shared/assets/icons/activity_addMember.svg?react';
import ActivityArchivedSVG from '@shared/assets/icons/activity_archived.svg?react';
import ActivityCloseSVG from '@shared/assets/icons/activty_close.svg?react';
import ActivityCreatedGroupSVG from '@shared/assets/icons/activity_createdGroup.svg?react';
import ActivityDeleteSVG from '@shared/assets/icons/activity_delete.svg?react';
import ActivityEditSVG from '@shared/assets/icons/activity_edit.svg?react';
import ActivityEditExpenseSVG from '@shared/assets/icons/activity_editExpense.svg?react';
import ActivityExpenseAddedSVG from '@shared/assets/icons/activity_expenseAdded.svg?react';
import ActivityPaidSVG from '@shared/assets/icons/activity_paid.svg?react';
import ActivityRemoveMemberSVG from '@shared/assets/icons/activity_removeMember.svg?react';
import ActivityReopenedSVG from '@shared/assets/icons/activity_reopened.svg?react';
import ActivityRestoredSVG from '@shared/assets/icons/activity_restored.svg?react';
import ActivitySetBySVG from '@shared/assets/icons/activity_setBy.svg?react';
import ActivityUpdateSVG from '@shared/assets/icons/activity_update.svg?react';
import ActivityUpdatedSVG from '@shared/assets/icons/activity_updated.svg?react';

export const DoubleArrowPrev = createIcon(DoubleArrowPrevSVG);
export const DoubleCheckIcon = createIcon(DoubleCheckIconSVG);
export const LogoutIcon = createIcon(LogoutIconSVG);
export const NavArrowIcon = createIcon(NavArrowIconSVG);
export const SettlupIcon = createIcon(SettlupIconSVG);
export const SplitAmountIcon = createIcon(SplitAmountIconSVG);
export const SplitEvenlyIcon = createIcon(SplitEvenlyIconSVG);
export const SplitPercentageIcon = createIcon(SplitPercentageIconSVG);
export const SplitSharesIcon = createIcon(SplitSharesIconSVG);
export const UploadCustom = createIcon(UploadCustomSVG);
export const AllListIcon = createIcon(AllListSVG);
export const BreakdownIcon = createIcon(BreakdownSVG);
export const BreakdownReverseIcon = createIcon(BreakupSVG);
export const LocationIcon = createIcon(LocationSVG);
export const ReceiptIcon = createIcon(ReceiptSVG);
export const UploadIcon = createIcon(UploadSVG);
export const HourglassTilted = createIcon(HourglassTiltedSVG);
export const Unassigned = createIcon(UnassignedSVG);
export const ListIcon = createIcon(ListIconSVG);
export const GoogleIcon = createIcon(GoogleIconSVG);
export const AppleIcon = createIcon(AppleIconSVG);
export const AddPlus = createIcon(AddPlusSVG);
export const RoundArrowBack = createIcon(RoundArrowBackSVG);
export const CheckboxTick = createIcon(checkboxTickSVG);
export const GroupTypeFriends = createIcon(GroupTypeFriendsSVG);
export const GroupTypeHome = createIcon(GroupTypeHomeSVG);
export const GroupTypeOffice = createIcon(GroupTypeOfficeSVG);
export const GroupTypeOthers = createIcon(GroupTypeOthersSVG);
export const GroupTypeRoommates = createIcon(GroupTypeRoommatesSVG);
export const GroupTypeTrip = createIcon(GroupTypeTripSVG);
export const FolderUpload = createIcon(UploadFolderSVG);
export const DownArrow = createIcon(DownArrowSVG);
export const UpArrow = createIcon(UpArrowSVG);
export const RightArrow = createIcon(RightArrowSVG);
export const Recycledollar = createIcon(recycledollarSVG);
export const ReportWarning = createIcon(reportWarningSVG);
export const MailEnvelope = createIcon(MailEnvelopeSVG);
export const HistoryIcon = createIcon(HistorySVG);
export const HistoryClockwiseIcon = createIcon(HistoryClockwiseSVG);
export const RepeatIcon = createIcon(RepeatIconSVG);
export const FoodIcon = createIcon(FoodIconSVG);
export const NavBarchartIcon = createIcon(NavBarchartIconSVG);
export const NavClockIcon = createIcon(NavClockIconSVG);
export const NavExclamationIcon = createIcon(NavExclamationIconSVG);
export const NavHourglassIcon = createIcon(NavHourglassIconSVG);
export const NavListpadIcon = createIcon(NavListpadIconSVG);
export const NavQuestionIcon = createIcon(NavQuestionIconSVG);
export const WalletIcon = createIcon(WalletIconSVG);
export const RepeatCalanderIcon = createIcon(RepeatCalanderIconSVG);
export const DiningIcon = createIcon(DiningIconSVG);
export const SnacksIcon = createIcon(SnacksIconSVG);
export const GroceriesIcon = createIcon(GroceriesIconSVG);
export const LiquorIcon = createIcon(LiquorIconSVG);
export const HouseIcon = createIcon(HouseIconSVG);
export const PowerIcon = createIcon(PowerIconSVG);
export const ExcelIcon = createIcon(ExcelIconSVG);
export const LightBulbOnIcon = createIcon(LightBulbOnSVG);

export const InfoInCircleIcon = createIcon(InfoInCircleSVG);
export const ThreeArrowForwordIcon = createIcon(ThreeArrowForwordSVG);
export const SingleStarIcon = createIcon(SingleStarSVG);
export const HeartIcon = createIcon(HeartSVG);
export const ActiveGroupStateIcon = createIcon(ActiveGroupStateSVG);
export const MonthlySettleStateIcon = createIcon(MonthlySettleStateSVG);
export const MonthlySpendingStateIcon = createIcon(MonthlySpendingStateSVG);
export const PendingStatementStateIcon = createIcon(PendingStatementStateSVG);
export const SettlementReceivedIcon = createIcon(SettlementReceivedSVG);
export const SettlementPaidIcon = createIcon(SettlementPaidSVG);
export const OtherExpenseIcon = createIcon(OtherExpenseSVG);
export const FundsAllocationIcon = createIcon(FundsAllocationIconSVG);
export const FundsExchangeIcon = createIcon(FundsExchangeIconSVG);
export const WalletDepositIcon = createIcon(WalletDepositIconSVG);
export const RepeatIcon2 = createIcon(RepeatIconSVG2);
export const TickCheckIcon = createIcon(tickCheckIconSVG);

export const ActivityAddedIcon = createIcon(ActivityAddedSVG);
export const ActivityAddMemberIcon = createIcon(ActivityAddMemberSVG);
export const ActivityArchivedIcon = createIcon(ActivityArchivedSVG);
export const ActivityCloseIcon = createIcon(ActivityCloseSVG);
export const ActivityCreatedGroupIcon = createIcon(ActivityCreatedGroupSVG);
export const ActivityDeleteIcon = createIcon(ActivityDeleteSVG);
export const ActivityEditIcon = createIcon(ActivityEditSVG);
export const ActivityEditExpenseIcon = createIcon(ActivityEditExpenseSVG);
export const ActivityExpenseAddedIcon = createIcon(ActivityExpenseAddedSVG);
export const ActivityPaidIcon = createIcon(ActivityPaidSVG);
export const ActivityRemoveMemberIcon = createIcon(ActivityRemoveMemberSVG);
export const ActivityReopenedIcon = createIcon(ActivityReopenedSVG);
export const ActivityRestoredIcon = createIcon(ActivityRestoredSVG);
export const ActivitySetByIcon = createIcon(ActivitySetBySVG);
export const ActivityUpdateIcon = createIcon(ActivityUpdateSVG);
export const ActivityUpdatedIcon = createIcon(ActivityUpdatedSVG);
